import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import * as locales from "@material-ui/core/locale";

import { Titulo } from "../components/Titulo";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import { TablaHead } from "../components/_dashboard/tablas";
import Page from "../components/Page";
import Label from "../components/Label";

// Importamos la API y el nuevo Hook
import { empleadosApi } from "src/services/empleadosApi";
import { useHistorialVisita } from "src/hooks/useHistorialVisita";
import ModalAccionEmpleado from "src/components/Modal/modalAccionEmpleado";
import { BRAND } from "../theme/brandTokens";

// Definición de las columnas de la tabla
const TABLE_HEAD = [
  { id: "refEmpleado", label: "Empleado", alignRight: false },
  { id: "urlAccion", label: "Acción / URL Visitada", alignRight: false },
  { id: "fechaAccion", label: "Fecha", alignRight: false },
  { id: "horaAccion", label: "Hora", alignRight: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function ActividadEmpleados() {
  const { empleado: user, sucursal } = useSelector((state) => state.auth.user);
  const sucursalActual = sucursal?.refSucursal || sucursal || null;

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("fechaAccion");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locale] = useState("esES");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Llamada al hook que obtiene TODO el historial de la tabla
  const { data: historial = [], isLoading: loadingTable } =
    empleadosApi.useObtenerUltimaAccionPorEmpleadoQuery(sucursalActual, {
      skip: !sucursalActual,
    });

  const { data: urlMasVisitada, isLoading: loadingUrlMasVisitada } =
    empleadosApi.useObtenerUrlMasVisitadaQuery(sucursalActual, {
      skip: !sucursalActual,
    });

  useHistorialVisita(user);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (row) => {
    setSelectedActivity(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedActivity(null);
  };

  // Ordenamiento dinámico en el cliente
  const sortedData = useMemo(() => {
    const sorted = [...historial].sort(getComparator(order, orderBy));
    return sorted;
  }, [historial, order, orderBy]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sortedData.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(0);
  }, [sortedData.length, rowsPerPage, page]);

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sortedData.length) : 0;

  const isHistoryNotFound = !loadingTable && sortedData.length === 0;
  const empleadoRefParaConsulta = useMemo(() => {
    if (!selectedActivity) return null;

    return (
      selectedActivity.refEmpleado ||
      selectedActivity.rut ||
      selectedActivity.empleado?.rut ||
      selectedActivity.empleadoRut ||
      null
    );
  }, [selectedActivity]);

  const {
    data: ultimasAccionesEmpleado = [],
    isLoading: loadingUltimasAcciones,
  } = empleadosApi.useObtenerUltimasAccionesEmpleadoQuery(
    { refEmpleado: empleadoRefParaConsulta, refSucursal: sucursalActual },
    {
      skip: !empleadoRefParaConsulta || !sucursalActual,
    },
  );

  const obtenerRutaDesdeTercerSlash = (url) => {
    if (!url) return "—";

    const partes = url.split("/");

    if (partes.length > 3) {
      return partes.slice(3).join("/");
    }

    return url;
  };

  const rawUrl =
    urlMasVisitada?.url ||
    urlMasVisitada?.urlAccion ||
    (typeof urlMasVisitada === "string" ? urlMasVisitada : null);
  const totalVisitas =
    urlMasVisitada?.total_visitas || urlMasVisitada?.totalVisitas || null;
  const rutaFormateada = obtenerRutaDesdeTercerSlash(rawUrl);

  return (
    <Page title="Historial de Actividad - TAXTIC">
      <Container maxWidth="xl">
        <Titulo>
          <Grid container>
            <Grid item xs={12}>
              <Typography variant="h4" gutterBottom>
                Historial de Actividad de Empleados
              </Typography>
            </Grid>
          </Grid>
        </Titulo>

        {/* ── URL MÁS VISITADA ── */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                {loadingUrlMasVisitada ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 3,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width={180} height={24} />
                      <Skeleton variant="text" width="45%" height={38} />
                    </Box>

                    <Skeleton variant="rectangular" width={120} height={65} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: {
                        xs: "flex-start",
                        sm: "center",
                      },
                      justifyContent: "space-between",
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                      },
                      gap: 2,
                    }}
                  >
                    {/* Información principal */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Actividad destacada
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 1,
                        }}
                      >
                        Sección más visitada
                      </Typography>

                      {rawUrl ? (
                        <Label
                          variant="ghost"
                          color="info"
                          title={rawUrl}
                          sx={{
                            display: "inline-block",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            px: 2,
                            py: 1,
                          }}
                        >
                          {rutaFormateada}
                        </Label>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No existen visitas registradas.
                        </Typography>
                      )}
                    </Box>

                    {/* Total de visitas */}
                    <Box
                      sx={{
                        minWidth: {
                          xs: "100%",
                          sm: 130,
                        },
                        textAlign: {
                          xs: "left",
                          sm: "center",
                        },
                        borderLeft: {
                          xs: "none",
                          sm: "1px solid",
                        },
                        borderTop: {
                          xs: "1px solid",
                          sm: "none",
                        },
                        borderColor: "divider",
                        pt: {
                          xs: 2,
                          sm: 0,
                        },
                        pl: {
                          xs: 0,
                          sm: 3,
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        {Number(totalVisitas || 0).toLocaleString("es-CL")}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        visitas registradas
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              {!loadingTable ? (
                <Table>
                  <TablaHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={historial.length}
                    numSelected={0}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {sortedData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row) => {
                        const {
                          id,
                          refEmpleado,
                          urlAccion,
                          horaAccion,
                          fechaAccion,
                          nombre,
                          apellidos,
                          foto_url,
                        } = row;

                        const nombreCompleto =
                          nombre && apellidos
                            ? `${nombre} ${apellidos}`
                            : refEmpleado;

                        const iniciales = nombre
                          ? nombre.charAt(0).toUpperCase()
                          : "?";

                        // ── OBTENEMOS SOLO LO QUE ESTÁ DESPUÉS DEL TERCER SLASH ──
                        const seccionFormateada =
                          obtenerRutaDesdeTercerSlash(urlAccion);

                        return (
                          <TableRow
                            hover
                            key={id}
                            tabIndex={-1}
                            onClick={() => handleOpenModal(row)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                <Avatar
                                  src={foto_url}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: BRAND.color.primary,
                                    fontSize: 14,
                                  }}
                                >
                                  {iniciales}
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {nombre}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {apellidos}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Label variant="ghost" color="info">
                                {/* Se muestra solo lo que viene después del 3er '/' */}
                                {seccionFormateada}
                              </Label>
                            </TableCell>

                            <TableCell>{fechaAccion || "—"}</TableCell>
                            <TableCell>{horaAccion || "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                  {isHistoryNotFound && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={4} sx={{ py: 3 }}>
                          <SearchNotFound searchQuery="" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="500px" />
              )}
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>

        <ModalAccionEmpleado
          open={modalOpen}
          onClose={handleCloseModal}
          selectedActivity={selectedActivity}
          ultimasAccionesEmpleado={ultimasAccionesEmpleado}
          loadingUltimasAcciones={loadingUltimasAcciones}
          obtenerRuta={obtenerRutaDesdeTercerSlash}
        />
      </Container>
    </Page>
  );
}
